import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { CheckCircle2, XCircle } from "lucide-react";
import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { type ProgramDetail, useProgram, useReviseProgram, useRun, useRuns, useStartRun } from "../../api/hooks";
import { Empty, ErrorState, Loading } from "../../components/states";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Field, Textarea } from "../../components/ui/fields";
import { formatInstant, formatValue } from "../../lib/format";
import { ReportView } from "../reports/report-view";
import { ParameterForm } from "../runs/parameter-form";
import { StatusBadge } from "./status-badge";

type Session = ProgramDetail["sessions"][number];

function SessionProgress({ session }: { session: Session }) {
  const { t } = useTranslation();
  const busy = session.status === "queued" || session.status === "running";
  return (
    <Card className="space-y-3">
      {busy ? <Loading label={session.status === "queued" ? t("program.authoringQueued") : t("program.authoring")} /> : null}
      {session.status === "failed" && session.error ? (
        <ErrorState title={t("program.sessionFailed")} error={new Error(`${session.error.message} (${session.error.code})`)} />
      ) : null}
      {session.attempts.length > 0 ? (
        <ol className="space-y-2 text-sm">
          {session.attempts.map((attempt) => (
            <li key={attempt.attempt} className="space-y-1">
              <p className="flex items-center gap-2">
                {attempt.ok ? <CheckCircle2 className="h-4 w-4 text-positive" aria-hidden /> : <XCircle className="h-4 w-4 text-negative" aria-hidden />}
                {t("program.attempt", { attempt: attempt.attempt })}: {attempt.ok ? t("program.attemptPassed") : t("program.attemptFailed")}
              </p>
              {attempt.problems.length > 0 ? (
                <ul className="ml-6 list-disc text-muted-foreground">
                  {attempt.problems.slice(0, 5).map((problem) => (
                    <li key={problem} className="break-words">
                      {problem}
                    </li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ol>
      ) : null}
    </Card>
  );
}

function ReviseForm({ program }: { program: ProgramDetail }) {
  const { t } = useTranslation();
  const revise = useReviseProgram(program.id);
  const schema = z.object({ request: z.string().trim().min(10, t("create.tooShort")).max(4000, t("create.tooLong")) });
  const form = useForm<z.infer<typeof schema>>({ resolver: zodResolver(schema), defaultValues: { request: program.current?.request ?? program.sessions[0]?.request ?? "" } });
  const submit = form.handleSubmit(async ({ request }) => {
    await revise.mutateAsync(request);
  });
  return (
    <details className="rounded-lg border border-border p-4">
      <summary className="cursor-pointer text-sm font-medium">{t("program.revise")}</summary>
      <form onSubmit={(event) => void submit(event)} className="mt-3 space-y-3" noValidate>
        <Field label={t("program.request")} htmlFor="revise-request" hint={t("program.reviseHelp")} error={form.formState.errors.request?.message}>
          <Textarea id="revise-request" rows={5} {...form.register("request")} />
        </Field>
        {revise.isError ? <ErrorState title={t("program.sessionFailed")} error={revise.error} /> : null}
        <Button type="submit" variant="outline" disabled={revise.isPending}>
          {t("program.reviseSubmit")}
        </Button>
      </form>
    </details>
  );
}

function Runs({ program, selected }: { program: ProgramDetail; selected: string | undefined }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const runs = useRuns(program.id, true);
  const start = useStartRun(program.id);
  if (!program.current) return <Empty>{t("program.noVersion")}</Empty>;
  const select = (run: string) => void navigate({ to: "/programs/$programId", params: { programId: program.id }, search: { run } });
  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <h2 className="font-semibold">{t("run.heading")}</h2>
        <ParameterForm
          key={program.currentVersion}
          parameters={program.current.parameters}
          pending={start.isPending}
          onRun={(params) => start.mutate(params, { onSuccess: ({ id }) => select(id) })}
        />
        {start.isError ? <ErrorState title={t("report.failed")} error={start.error} /> : null}
      </section>
      <section className="space-y-2">
        <h2 className="font-semibold">{t("run.history")}</h2>
        {runs.isPending ? <Loading /> : null}
        {runs.isError ? <ErrorState title={t("report.failed")} error={runs.error} onRetry={() => void runs.refetch()} /> : null}
        {runs.data?.length === 0 ? <Empty>{t("run.noRuns")}</Empty> : null}
        <ul className="space-y-1">
          {runs.data?.map((run) => (
            <li key={run.id}>
              <button
                type="button"
                onClick={() => select(run.id)}
                aria-current={run.id === selected}
                className={`flex w-full flex-col gap-1 rounded-md px-3 py-2 text-left text-sm hover:bg-accent ${run.id === selected ? "bg-accent" : ""}`}
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">{formatInstant(run.createdAt)}</span>
                  <StatusBadge status={run.status} kind="run" />
                </span>
                <span className="truncate">{Object.entries(run.params).map(([key, value]) => `${key}: ${formatValue(value)}`).join(" · ")}</span>
              </button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function SelectedReport({ runId }: { runId: string | undefined }) {
  const { t } = useTranslation();
  const run = useRun(runId);
  const report = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (runId && !run.isPending && window.matchMedia("(max-width: 1279px)").matches) report.current?.scrollIntoView({ block: "start" });
  }, [runId, run.isPending]);
  return <div ref={report} className="scroll-mt-20">
    {!runId ? <Empty>{t("report.empty")}</Empty> : run.isPending ? <Loading />
      : run.isError ? <ErrorState title={t("report.failed")} error={run.error} onRetry={() => void run.refetch()} />
        : <ReportView run={run.data} />}
  </div>;
}

export function ProgramPage({ programId, runId }: { programId: string; runId: string | undefined }) {
  const { t } = useTranslation();
  const program = useProgram(programId);
  if (program.isPending) return <Loading />;
  if (program.isError) return <ErrorState title={t("program.loadFailed")} error={program.error} onRetry={() => void program.refetch()} />;
  const detail = program.data;
  const latest = detail.sessions[0];
  return (
    <div className="grid grid-cols-1 xl:grid-cols-2">
      <div className="min-w-0 space-y-6 p-6 xl:border-r xl:border-border">
        <header className="space-y-2">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-xl font-semibold">{detail.title}</h1>
            <StatusBadge status={detail.status} kind="program" />
            {detail.currentVersion ? <span className="text-sm text-muted-foreground">{t("programs.version", { version: detail.currentVersion })}</span> : null}
          </div>
          {detail.current?.description ? <p className="text-sm text-muted-foreground">{detail.current.description}</p> : null}
        </header>
        <section className="space-y-1">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{t("program.request")}</h2>
          <p className="whitespace-pre-wrap text-sm">{detail.current?.request ?? latest?.request}</p>
        </section>
        {detail.current?.agent ? (
          <section className="space-y-3 rounded-lg border border-border p-4 text-sm">
            <h2 className="font-semibold">{t("program.agentSetup")}</h2>
            <p className="text-muted-foreground">{t("program.agentHarness", { model: detail.current.agent.harness.model ?? t("program.operatorModel") })}</p>
            <details>
              <summary className="cursor-pointer font-medium">{t("program.runPrompt")}</summary>
              <p className="mt-2 whitespace-pre-wrap">{detail.current.agent.prompt}</p>
            </details>
            <details>
              <summary className="cursor-pointer font-medium">{t("program.skills", { count: detail.current.agent.skills.length })}</summary>
              {detail.current.agent.skills.length === 0 ? <p className="mt-2 text-muted-foreground">{t("program.noSkills")}</p> : null}
              {detail.current.agent.skills.map((skill) => <div key={skill.name} className="mt-2"><p className="font-medium">{skill.name}</p><p className="mt-1 whitespace-pre-wrap text-muted-foreground">{skill.content}</p></div>)}
            </details>
            <details>
              <summary className="cursor-pointer font-medium">{t("program.tools")}</summary>
              <ul className="mt-2 flex flex-wrap gap-2">{detail.current.agent.tools.map((tool) => <li key={tool} className="rounded bg-muted px-2 py-1 font-mono text-xs">{tool}</li>)}</ul>
            </details>
          </section>
        ) : null}
        {latest && (latest.status !== "ready" || latest.attempts.some((attempt) => !attempt.ok)) ? <SessionProgress session={latest} /> : null}
        <Runs program={detail} selected={runId} />
        {detail.current ? <ReviseForm key={detail.currentVersion} program={detail} /> : null}
        {detail.current ? (
          <details className="text-sm">
            <summary className="cursor-pointer text-muted-foreground">{t("program.files")}</summary>
            {Object.entries(detail.current.files).map(([name, content]) => (
              <div key={name} className="mt-3">
                <p className="font-mono text-xs text-muted-foreground">{name}</p>
                <pre className="mt-1 max-h-96 overflow-auto rounded-md bg-muted p-3 text-xs">{content}</pre>
              </div>
            ))}
          </details>
        ) : null}
      </div>
      <div className="min-w-0 p-6">
        <SelectedReport runId={runId} />
      </div>
    </div>
  );
}
